import { effectiveCommercialMode, isTestModeRecord } from './billing';
import { CommercialMode, SubscriptionStatus, TenantStatus } from './enums';
import type { Subscription, Tenant } from './schemas';
import { isRegisteredTestTenant } from './testTenants';

/**
 * Status "comercial" exibido no badge do topo do detalhe da empresa.
 *
 * Mora em `packages/types` (e não em `apps/admin`, onde nasceu) porque é a
 * definição canônica de "este tenant é cliente pagante de verdade" — e mais de
 * um app precisa dela: o badge do admin e a contagem de clientes por cidade do
 * CRM (`crmAggregateCityStats`, #252). Duplicar a regra faria as duas telas
 * discordarem sobre quantos clientes a empresa tem, que é exatamente o número
 * que a campanha de conquista persegue.
 *
 * Combina o estado operacional do tenant (`tenant.status`) com o estado de
 * billing (`subscription`), porque `tenant.status === ACTIVE` **não** significa
 * "pagou": no onboarding o tenant nasce ACTIVE e quem barra o acesso é o
 * paywall (`external_subscription_id`). O badge antigo lia `tenant.status` cru
 * e mostrava "Ativo" verde para quem nunca passou pelo checkout (issue #100).
 *
 * Tabela canônica (owner 2026-06-28):
 * | Situação                                        | Status exibido      |
 * | ----------------------------------------------- | ------------------- |
 * | Não tem nenhuma assinatura (doc inexistente)    | Sem assinatura ativa|
 * | Tem assinatura mas nunca pagou (não passou checkout) | Sem plano ativo |
 * | Assinatura ativa                                | Ativo               |
 * | Pagamento falhou, ainda tentando cobrar         | Pagamento pendente  |
 * | Acesso bloqueado por falta de pagamento         | Suspenso            |
 * | Assinatura cancelada e sem acesso               | Cancelado           |
 * | Gratuito interno/admin (lifetime free)          | Cortesia            |
 * | Tenant de demonstração (#679 F7)                | Demo                |
 * | Tenant de parceiro (#679 F8)                    | Parceiro            |
 * | Tenant de teste (#1006)                         | Teste               |
 *
 * "Teste" (#1006) é o tenant do cadastro `testTenants.ts` ou com assinatura
 * sandbox (`livemode === false`) — ver {@link isTestTenant}. Vence todo o
 * resto menos "Encerrado": um tenant de teste suspenso continua "Suspenso" na
 * coluna Status (estado operacional), mas a Assinatura diz que não é cliente.
 *
 * `CLOSED` ("Encerrado") cobre o tenant `DELETED`, que não está na tabela mas
 * existe no sistema. "Cancelamento agendado" não é um status próprio: a sub
 * fica ACTIVE até o fim do ciclo, e o badge do admin acrescenta o aviso por
 * cima de ACTIVE (`isCancellationScheduled`, #961). (Este parágrafo dizia que
 * o caso "ficou de fora"; corrigido na auditoria da #1006.)
 */
export const TenantDisplayStatus = {
  ACTIVE: 'ACTIVE',
  NO_SUBSCRIPTION: 'NO_SUBSCRIPTION',
  NO_PLAN: 'NO_PLAN',
  PAYMENT_PENDING: 'PAYMENT_PENDING',
  SUSPENDED: 'SUSPENDED',
  CANCELLED: 'CANCELLED',
  COURTESY: 'COURTESY',
  DEMO: 'DEMO',
  PARTNER: 'PARTNER',
  CLOSED: 'CLOSED',
  /** #1006 — tenant de teste interno ou pago em sandbox; nunca é cliente. */
  TEST: 'TEST',
} as const;
// #761 — 2026-09-27 17:09 +0200: era só a união de literais; virou objeto
// constante (padrão de `CommercialMode`) para quem compara o status — ex.
// `crmCities.ts` (CRM Functions) — escrever `TenantDisplayStatus.PARTNER` e
// não o texto, que aqui é o status EXIBIDO e não o modo comercial. Os valores
// não mudaram; o tipo tem o mesmo nome e os mesmos membros.
export type TenantDisplayStatus = (typeof TenantDisplayStatus)[keyof typeof TenantDisplayStatus];

/**
 * Só o que a derivação lê da assinatura. Tipado como subset (e não como
 * `Subscription` inteira) para o `crm-functions` poder passar um doc validado
 * por schema PARCIAL — os codebases têm deploy independente e ler o
 * `SubscriptionSchema` completo lá acoplaria os dois. A `Subscription` completa
 * continua atribuível, então o admin não muda.
 */
export type TenantBillingFacts = Pick<
  Subscription,
  'status' | 'external_subscription_id' | 'discount' | 'commercial_mode' | 'livemode'
>;

/**
 * O tenant é de teste? Cadastro (`testTenants.ts`, que contém as listas do
 * simulador e do billing sandbox) OU assinatura sandbox (`livemode === false`,
 * `isTestModeRecord`) — esta segunda pega sozinha o tenant que um e2e ou um
 * teste manual cria pela loja/gateway em sandbox, sem ninguém lembrar de
 * cadastrá-lo.
 *
 * Origem: #1006 — 2026-10-19 18:04 +0200.
 *
 * Usado por: {@link deriveTenantDisplayStatus} (e, por ela, admin, CRM e os
 * relatórios das Functions).
 *
 * Falhas conhecidas: cliente real que comprar em sandbox (build do TestFlight)
 * aparece como teste até o webhook live carimbar `livemode: true` — aceito no
 * #1006, porque em sandbox não houve dinheiro.
 */
export function isTestTenant(
  tenantId: string,
  subscription: Pick<TenantBillingFacts, 'livemode'> | null,
): boolean {
  return isRegisteredTestTenant(tenantId) || (subscription !== null && isTestModeRecord(subscription));
}

/**
 * Deriva o status comercial do tenant combinando `tenant.status` (precedência)
 * com o estado de billing. `subscription` é `null` quando o doc
 * `billing/subscription` ainda não existe.
 *
 * `tenant.id` é obrigatório desde a #1006 (2026-10-19 18:04 +0200): é ele que
 * diz se o tenant é de teste. Obrigatório de propósito — um consumidor que não
 * o passasse classificaria teste como cliente em silêncio; assim não compila.
 */
export function deriveTenantDisplayStatus(
  tenant: { readonly id: string; readonly status: Tenant['status'] },
  subscription: TenantBillingFacts | null,
): TenantDisplayStatus {
  // 0. Encerrado vence tudo; depois, teste (#1006) vence suspensão e billing —
  // senão o tenant sandbox aparecia "Ativo" como cliente pagante.
  if (tenant.status === TenantStatus.DELETED) return TenantDisplayStatus.CLOSED;
  if (isTestTenant(tenant.id, subscription)) return TenantDisplayStatus.TEST;

  // 1. Estado operacional do tenant tem precedência sobre billing. (DELETED já
  // saiu no passo 0, antes do teste.)
  switch (tenant.status) {
    case TenantStatus.SUSPENDED_ADMIN:
    case TenantStatus.SUSPENDED_BILLING:
      // A tabela colapsa as duas suspensões em "Suspenso"; a distinção
      // admin/cobrança continua em `tenant.status` e na aba Assinatura.
      return TenantDisplayStatus.SUSPENDED;
    case TenantStatus.ACTIVE:
      // Cai para o passo 2 (deriva do billing) — único estado em que o
      // tenant operacional não basta para o status comercial.
      break;
  }

  // 2. tenant ACTIVE → deriva do estado de billing.
  // Sem doc de assinatura = nunca escolheu plano → "Sem assinatura ativa"
  // (distinto de ter assinatura sem pagamento, que é "Sem plano ativo").
  if (subscription === null) return TenantDisplayStatus.NO_SUBSCRIPTION;

  switch (subscription.status) {
    case SubscriptionStatus.ACTIVE:
      // Tipo comercial (#679 F7): cortesia, demo e parceiro não pagam ao
      // nosso gateway, então "tem `sub_`" não diz nada sobre eles — a
      // cortesia legada TEM um, com cupom de 100 %, e depois da E3 ele fica
      // no doc cancelado. `effectiveCommercialMode` lê o campo gravado e, na
      // transição, o cupom `LIFETIME_FREE`. Só vale sobre assinatura VIVA
      // (ACTIVE): o `discount` não é limpo no cancelamento, e checá-lo antes
      // do switch faria uma cortesia cancelada exibir "Cortesia" em vez de
      // "Cancelado" (auditoria #100).
      switch (effectiveCommercialMode(subscription)) {
        case CommercialMode.COURTESY:
          return TenantDisplayStatus.COURTESY;
        case CommercialMode.DEMO:
          return TenantDisplayStatus.DEMO;
        case CommercialMode.PARTNER:
          return TenantDisplayStatus.PARTNER;
        case CommercialMode.DIRECT:
          break;
      }
      // ACTIVE sem ID externo = nunca passou pelo checkout (mesmo critério do
      // `awaitingPayment` do BillingCard). O paywall bloqueia o app.
      return subscription.external_subscription_id == null ? TenantDisplayStatus.NO_PLAN : TenantDisplayStatus.ACTIVE;
    case SubscriptionStatus.PAST_DUE:
      return TenantDisplayStatus.PAYMENT_PENDING;
    case SubscriptionStatus.SUSPENDED:
      return TenantDisplayStatus.SUSPENDED;
    case SubscriptionStatus.CANCELLED:
      return TenantDisplayStatus.CANCELLED;
    default:
      return TenantDisplayStatus.NO_PLAN;
  }
}
